import html2canvas from "html2canvas";

export const EXPORT_CAPTURE_WIDTH = 1440;

interface ExportTAccountLineEntry {
  debitAmount?: number;
  creditAmount?: number;
  displayLetter?: string;
  displayReference?: string;
  isBeginningBalance?: boolean;
  isUnbalanced?: boolean;
}

interface ExportTAccount {
  accountName: string;
  entries: ExportTAccountLineEntry[];
}

interface ExportEntryListRow {
  displayLetter: string;
  account: string;
  debitAmount?: number;
  creditAmount?: number;
  comment: string;
}

interface ExportUnbalancedRow {
  letter: string;
  debitSubtotal: number;
  creditSubtotal: number;
}

export function applyExportCloneAdjustments(clonedDocument: Document): void {
  const exportRoot = clonedDocument.querySelector("[data-sheet-export-root]") as HTMLElement | null;
  if (exportRoot) {
    exportRoot.style.flex = "none";
    exportRoot.style.width = `${EXPORT_CAPTURE_WIDTH}px`;
    exportRoot.style.minWidth = `${EXPORT_CAPTURE_WIDTH}px`;
    exportRoot.style.maxWidth = `${EXPORT_CAPTURE_WIDTH}px`;
  }

  const borderElements = clonedDocument.querySelectorAll(".export-hide-divider");
  borderElements.forEach((element) => {
    const htmlElement = element as HTMLElement;
    htmlElement.style.borderLeft = "none";
    htmlElement.style.borderLeftWidth = "0px";
    htmlElement.style.borderRight = "none";
    htmlElement.style.borderRightWidth = "0px";
  });

  const constrainedHeightElements = clonedDocument.querySelectorAll(".export-expand-height");
  constrainedHeightElements.forEach((element) => {
    const htmlElement = element as HTMLElement;
    htmlElement.style.maxHeight = "none";
    htmlElement.style.height = "auto";
  });

  const scrollableElements = clonedDocument.querySelectorAll(".export-expand-scroll");
  scrollableElements.forEach((element) => {
    const htmlElement = element as HTMLElement;
    htmlElement.style.maxHeight = "none";
    htmlElement.style.height = "auto";
    htmlElement.style.overflow = "visible";
    htmlElement.style.overflowY = "visible";
  });
}

function createExportCanvas(sourceCanvas: HTMLCanvasElement): HTMLCanvasElement {
  const horizontalMargin = 24;
  const bottomMargin = 24;
  const paddedCanvas = document.createElement("canvas");
  paddedCanvas.width = sourceCanvas.width + (horizontalMargin * 2);
  paddedCanvas.height = sourceCanvas.height + bottomMargin;

  const context = paddedCanvas.getContext("2d");
  if (!context) {
    throw new Error("Unable to prepare export image.");
  }

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, paddedCanvas.width, paddedCanvas.height);
  context.drawImage(sourceCanvas, horizontalMargin, 0);

  return paddedCanvas;
}

async function captureSheetCanvas(exportArea: HTMLElement): Promise<HTMLCanvasElement> {
  const sourceCanvas = await html2canvas(exportArea, {
    backgroundColor: "#ffffff",
    scale: 2,
    windowWidth: EXPORT_CAPTURE_WIDTH,
    useCORS: true,
    logging: false,
    ignoreElements: (element) => element.classList.contains("export-exclude"),
    onclone: (clonedDocument) => {
      applyExportCloneAdjustments(clonedDocument);
    },
  });

  return createExportCanvas(sourceCanvas);
}

export function getExportBaseFileName(documentTitle: string): string {
  const rawTitle = documentTitle.trim();
  const fallbackTitle = "taccounts-sheet";
  const normalizedTitle = rawTitle || fallbackTitle;
  const sanitizedTitle = normalizedTitle
    .replace(/[<>:"/\\|?*]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return sanitizedTitle || fallbackTitle;
}

export async function exportSheetAsPNG(exportArea: HTMLElement, fileBaseName: string): Promise<void> {
  const canvas = await captureSheetCanvas(exportArea);
  const imageData = canvas.toDataURL("image/png");
  const anchor = document.createElement("a");
  anchor.href = imageData;
  anchor.download = `${fileBaseName}.png`;
  anchor.click();
}

export async function exportSheetAsPDF(exportArea: HTMLElement, fileBaseName: string): Promise<void> {
  const canvas = await captureSheetCanvas(exportArea);
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({
    // T-account exports are table-heavy; landscape keeps text from becoming too small.
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const scaledHeight = (canvas.height * pageWidth) / canvas.width;

  if (scaledHeight <= pageHeight) {
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, pageWidth, scaledHeight, undefined, "FAST");
  } else {
    const sourceSliceHeight = Math.floor((pageHeight * canvas.width) / pageWidth);
    let sourceY = 0;
    let isFirstPage = true;

    while (sourceY < canvas.height) {
      const currentSliceHeight = Math.min(sourceSliceHeight, canvas.height - sourceY);
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = canvas.width;
      pageCanvas.height = currentSliceHeight;

      const pageContext = pageCanvas.getContext("2d");
      if (!pageContext) {
        throw new Error("Unable to prepare PDF export.");
      }

      pageContext.fillStyle = "#ffffff";
      pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      pageContext.drawImage(
        canvas,
        0,
        sourceY,
        canvas.width,
        currentSliceHeight,
        0,
        0,
        pageCanvas.width,
        pageCanvas.height,
      );

      const renderedSliceHeight = (currentSliceHeight * pageWidth) / canvas.width;

      if (!isFirstPage) {
        pdf.addPage();
      }

      pdf.addImage(pageCanvas.toDataURL("image/png"), "PNG", 0, 0, pageWidth, renderedSliceHeight, undefined, "FAST");
      sourceY += currentSliceHeight;
      isFirstPage = false;
    }
  }

  pdf.save(`${fileBaseName}.pdf`);
}

export async function exportSheetAsXLSX(options: {
  fileBaseName: string;
  forceTwoDecimalDisplay: boolean;
  accountsPerRow?: number;
  entryListSeparateSheet?: boolean;
  formatNumbersWithCommas?: boolean;
  tAccounts: ExportTAccount[];
  entryListRows: ExportEntryListRow[];
  unbalancedRows: ExportUnbalancedRow[];
}): Promise<void> {
  const {
    fileBaseName,
    forceTwoDecimalDisplay,
    accountsPerRow: configuredAccountsPerRow,
    entryListSeparateSheet = true,
    formatNumbersWithCommas = false,
    tAccounts,
    entryListRows,
    unbalancedRows,
  } = options;
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("T-Accounts", {
    views: [{ showGridLines: false }],
  });

  const lineBorder = {
    style: "medium" as const,
    color: { argb: "FF2E2E2E" },
  };
  const subtotalLineBorder = {
    style: "thin" as const,
    color: { argb: "FF2E2E2E" },
  };
  const gridBorder = {
    style: "thin" as const,
    color: { argb: "FFD0D0D0" },
  };
  const unbalancedLabelFontColor = { argb: "FFD32F2F" };
  const accountsPerRow = Math.max(1, Math.min(10, Math.round(configuredAccountsPerRow ?? 4)));
  const tAccountStartCol = 2;
  const tAccountStartRow = 3;
  const accountTableWidth = 4;
  const tableGapColumns = 1;
  const rowGap = 2;
  const baseAmountFormat = formatNumbersWithCommas
    ? (forceTwoDecimalDisplay ? "#,##0.00" : "#,##0")
    : (forceTwoDecimalDisplay ? "0.00" : "0");

  const titleCell = worksheet.getCell(1, 2);
  titleCell.value = fileBaseName;
  titleCell.font = { bold: true, size: 14 };
  titleCell.alignment = { horizontal: "left", vertical: "middle" };

  let tAccountsMaxCol = tAccountStartCol + accountTableWidth - 1;
  let tAccountsMaxRow = tAccountStartRow;

  for (let chunkStart = 0, startRow = tAccountStartRow; chunkStart < tAccounts.length; chunkStart += accountsPerRow) {
    const chunk = tAccounts.slice(chunkStart, chunkStart + accountsPerRow);
    const chunkMaxRows = Math.max(...chunk.map((item) => Math.max(item.entries.length, 1)));

    for (let indexInChunk = 0; indexInChunk < chunk.length; indexInChunk += 1) {
      const item = chunk[indexInChunk];
      const startCol = tAccountStartCol + (indexInChunk * (accountTableWidth + tableGapColumns));
      const titleRow = startRow;
      const topLineRow = startRow + 1;
      const firstAmountRow = topLineRow;
      const debitLabelCol = startCol;
      const debitAmountCol = startCol + 1;
      const creditAmountCol = startCol + 2;
      const creditLabelCol = startCol + 3;

      worksheet.getColumn(debitLabelCol).width = 8;
      worksheet.getColumn(debitAmountCol).width = 12;
      worksheet.getColumn(creditAmountCol).width = 12;
      worksheet.getColumn(creditLabelCol).width = 8;

      worksheet.mergeCells(titleRow, debitAmountCol, titleRow, creditAmountCol);
      const titleCell = worksheet.getCell(titleRow, debitAmountCol);
      titleCell.value = item.accountName;
      titleCell.font = { bold: true, size: 12 };
      titleCell.alignment = { horizontal: "center", vertical: "middle" };

      const topDebitAmountCell = worksheet.getCell(topLineRow, debitAmountCol);
      const topCreditAmountCell = worksheet.getCell(topLineRow, creditAmountCol);
      topDebitAmountCell.border = { top: lineBorder, right: lineBorder };
      topCreditAmountCell.border = { top: lineBorder };

      const hasEntries = item.entries.length > 0;
      const debitEntries = item.entries.filter((entry) => entry.debitAmount !== undefined);
      const creditEntries = item.entries.filter((entry) => entry.creditAmount !== undefined);
      const lineItemRowCount = Math.max(debitEntries.length, creditEntries.length, 2);
      const debitLineRows = Array.from({ length: lineItemRowCount }, (_, rowIndex) => debitEntries[rowIndex] ?? null);
      const creditLineRows = Array.from({ length: lineItemRowCount }, (_, rowIndex) => creditEntries[rowIndex] ?? null);
      const hasDebitEntries = debitEntries.length > 0;
      const hasCreditEntries = creditEntries.length > 0;
      const debitSubtotal = debitEntries.reduce((total, entry) => total + (entry.debitAmount ?? 0), 0);
      const creditSubtotal = creditEntries.reduce((total, entry) => total + (entry.creditAmount ?? 0), 0);
      const endingBalance = creditSubtotal - debitSubtotal;
      const endingBalanceAmount = Math.abs(endingBalance);
      const getLineItemLabel = (entry: ExportTAccountLineEntry): string => entry.displayLetter ?? entry.displayReference ?? "";

      for (let rowIndex = 0; rowIndex < lineItemRowCount; rowIndex += 1) {
        const debitEntry = debitLineRows[rowIndex];
        const creditEntry = creditLineRows[rowIndex];
        const currentRow = firstAmountRow + rowIndex;
        const debitLabelCell = worksheet.getCell(currentRow, debitLabelCol);
        const debitAmountCell = worksheet.getCell(currentRow, debitAmountCol);
        const creditAmountCell = worksheet.getCell(currentRow, creditAmountCol);
        const creditLabelCell = worksheet.getCell(currentRow, creditLabelCol);

        debitAmountCell.border = {
          ...(debitAmountCell.border ?? {}),
          right: lineBorder,
        };

        if (debitEntry?.debitAmount !== undefined) {
          debitLabelCell.value = getLineItemLabel(debitEntry);
          debitLabelCell.alignment = { horizontal: "right", vertical: "middle" };
          if (debitEntry.isUnbalanced) {
            debitLabelCell.font = { color: unbalancedLabelFontColor };
          }
          debitAmountCell.value = debitEntry.debitAmount;
          debitAmountCell.alignment = { horizontal: "right", vertical: "middle" };
          debitAmountCell.numFmt = baseAmountFormat;
        }

        if (creditEntry?.creditAmount !== undefined) {
          creditAmountCell.value = creditEntry.creditAmount;
          creditAmountCell.alignment = { horizontal: "right", vertical: "middle" };
          creditAmountCell.numFmt = baseAmountFormat;
          creditLabelCell.value = getLineItemLabel(creditEntry);
          creditLabelCell.alignment = { horizontal: "left", vertical: "middle" };
          if (creditEntry.isUnbalanced) {
            creditLabelCell.font = { color: unbalancedLabelFontColor };
          }
        }

        if (!debitEntry) {
          debitAmountCell.alignment = { horizontal: "right", vertical: "middle" };
        }

        if (!creditEntry) {
          creditAmountCell.alignment = { horizontal: "right", vertical: "middle" };
        }
      }

      const subtotalRow = firstAmountRow + lineItemRowCount;
      const subtotalDebitAmountCell = worksheet.getCell(subtotalRow, debitAmountCol);
      const subtotalCreditAmountCell = worksheet.getCell(subtotalRow, creditAmountCol);
      subtotalDebitAmountCell.border = { top: subtotalLineBorder, right: lineBorder };
      subtotalCreditAmountCell.border = { top: subtotalLineBorder };

      if (hasEntries && hasDebitEntries) {
        subtotalDebitAmountCell.value = debitSubtotal;
        subtotalDebitAmountCell.alignment = { horizontal: "right", vertical: "middle" };
        subtotalDebitAmountCell.numFmt = baseAmountFormat;
        subtotalDebitAmountCell.font = { bold: true };
      }

      if (hasEntries && hasCreditEntries) {
        subtotalCreditAmountCell.value = creditSubtotal;
        subtotalCreditAmountCell.alignment = { horizontal: "right", vertical: "middle" };
        subtotalCreditAmountCell.numFmt = baseAmountFormat;
        subtotalCreditAmountCell.font = { bold: true };
      }

      const endingBalanceRow = subtotalRow + 1;
      const endingBalanceDebitLabelCell = worksheet.getCell(endingBalanceRow, debitLabelCol);
      const endingBalanceDebitAmountCell = worksheet.getCell(endingBalanceRow, debitAmountCol);
      const endingBalanceCreditAmountCell = worksheet.getCell(endingBalanceRow, creditAmountCol);
      const endingBalanceCreditLabelCell = worksheet.getCell(endingBalanceRow, creditLabelCol);
      endingBalanceDebitAmountCell.border = { top: subtotalLineBorder, right: lineBorder };
      endingBalanceCreditAmountCell.border = { top: subtotalLineBorder };

      if (endingBalance < 0) {
        endingBalanceDebitLabelCell.value = "EBal";
        endingBalanceDebitLabelCell.alignment = { horizontal: "right", vertical: "middle" };
        endingBalanceDebitAmountCell.value = endingBalanceAmount;
        endingBalanceDebitAmountCell.alignment = { horizontal: "right", vertical: "middle" };
        endingBalanceDebitAmountCell.numFmt = baseAmountFormat;
      } else {
        endingBalanceCreditAmountCell.value = endingBalanceAmount;
        endingBalanceCreditAmountCell.alignment = { horizontal: "right", vertical: "middle" };
        endingBalanceCreditAmountCell.numFmt = baseAmountFormat;
        endingBalanceCreditLabelCell.value = "EBal";
        endingBalanceCreditLabelCell.alignment = { horizontal: "left", vertical: "middle" };
      }

      const accountRightCol = creditLabelCol;
      const accountBottomRow = Math.max(topLineRow, endingBalanceRow);
      tAccountsMaxCol = Math.max(tAccountsMaxCol, accountRightCol);
      tAccountsMaxRow = Math.max(tAccountsMaxRow, accountBottomRow);
    }

    startRow += 1 + Math.max(chunkMaxRows, 2) + 2 + rowGap;
  }

  const entryListWorksheet = entryListSeparateSheet
    ? workbook.addWorksheet("Entries", {
      views: [{ showGridLines: false }],
    })
    : worksheet;
  const entryListStartCol = entryListSeparateSheet ? 2 : tAccountsMaxCol + 2;
  const entryListStartRow = entryListSeparateSheet ? 2 : tAccountsMaxRow + 2;
  const entryListHeaders = ["Ref", "Account", "Debit", "Credit", "Comment"];
  const entryListColumnWidths = [8, 22, 12, 12, 30];

  entryListColumnWidths.forEach((width, index) => {
    entryListWorksheet.getColumn(entryListStartCol + index).width = width;
  });

  const entryTitleCell = entryListWorksheet.getCell(
    entryListSeparateSheet ? 1 : Math.max(1, entryListStartRow - 1),
    entryListStartCol,
  );
  entryTitleCell.value = fileBaseName;
  entryTitleCell.font = { bold: true, size: 14 };
  entryTitleCell.alignment = { horizontal: "left", vertical: "middle" };

  entryListHeaders.forEach((header, index) => {
    const headerCell = entryListWorksheet.getCell(entryListStartRow, entryListStartCol + index);
    headerCell.value = header;
    headerCell.font = { bold: true };
    headerCell.alignment = {
      horizontal: index === 2 || index === 3 ? "right" : "left",
      vertical: "middle",
    };
    headerCell.border = {
      top: gridBorder,
      bottom: gridBorder,
    };
  });

  entryListRows.forEach((row, rowIndex) => {
    const worksheetRow = entryListStartRow + 1 + rowIndex;
    const values: Array<string | number | undefined> = [
      row.displayLetter,
      row.account,
      row.debitAmount,
      row.creditAmount,
      row.comment,
    ];

    values.forEach((value, columnIndex) => {
      const cell = entryListWorksheet.getCell(worksheetRow, entryListStartCol + columnIndex);
      cell.value = value ?? "";
      cell.alignment = {
        horizontal: columnIndex === 2 || columnIndex === 3 ? "right" : "left",
        vertical: "middle",
      };

      if ((columnIndex === 2 || columnIndex === 3) && typeof value === "number") {
        cell.numFmt = baseAmountFormat;
      }
    });
  });

  const unbalancedWorksheet = workbook.addWorksheet("Unbalanced", {
    views: [{ showGridLines: false }],
  });
  const unbalancedStartCol = 2;
  const unbalancedStartRow = 2;
  const unbalancedHeaders = ["Ref", "Debits", "Credits"];
  const unbalancedColumnWidths = [8, 12, 12];

  const unbalancedTitleCell = unbalancedWorksheet.getCell(1, 2);
  unbalancedTitleCell.value = fileBaseName;
  unbalancedTitleCell.font = { bold: true, size: 14 };
  unbalancedTitleCell.alignment = { horizontal: "left", vertical: "middle" };

  unbalancedColumnWidths.forEach((width, index) => {
    unbalancedWorksheet.getColumn(unbalancedStartCol + index).width = width;
  });

  unbalancedHeaders.forEach((header, index) => {
    const headerCell = unbalancedWorksheet.getCell(unbalancedStartRow, unbalancedStartCol + index);
    headerCell.value = header;
    headerCell.font = { bold: true };
    headerCell.alignment = {
      horizontal: index === 1 || index === 2 ? "right" : "left",
      vertical: "middle",
    };
    headerCell.border = {
      top: gridBorder,
      bottom: gridBorder,
    };
  });

  unbalancedRows.forEach((row, rowIndex) => {
    const worksheetRow = unbalancedStartRow + 1 + rowIndex;

    const rowValues: Array<string | number> = [
      row.letter,
      row.debitSubtotal,
      row.creditSubtotal,
    ];

    rowValues.forEach((value, columnIndex) => {
      const cell = unbalancedWorksheet.getCell(worksheetRow, unbalancedStartCol + columnIndex);
      cell.value = value;
      cell.alignment = {
        horizontal: columnIndex === 1 || columnIndex === 2 ? "right" : "left",
        vertical: "middle",
      };

      if ((columnIndex === 1 || columnIndex === 2) && typeof value === "number") {
        cell.numFmt = baseAmountFormat;
      }
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob(
    [buffer],
    { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
  );
  const downloadUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = `${fileBaseName}.xlsx`;
  anchor.click();
  URL.revokeObjectURL(downloadUrl);
}
